import React, { useEffect, useState } from "react";
import "@/index.css";
import "@/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { BrandThemeProvider } from "@/contexts/BrandThemeContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PermissionsProvider } from "@/contexts/PermissionsContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { usePageTracking } from "@/hooks/usePageTracking";

// Pages
import LandingPage from "@/pages/LandingPage";
import AboutPage from "@/pages/AboutPage";
import FeaturesPage from "@/pages/FeaturesPage";
import CustomersPage from "@/pages/CustomersPage";
import { VsABCmouse, VsHomer, VsKhanKids } from "@/pages/VsPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import OrganizationRegister from "@/pages/OrganizationRegister";
import HomeschoolRegister from "@/pages/HomeschoolRegister";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ParentDashboard from "@/pages/ParentDashboard";
import StudentDashboard from "@/pages/StudentDashboard";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import ChristianAdminLogin from "@/pages/ChristianAdminLogin";
import ChristianAdminDashboard from "@/pages/ChristianAdminDashboard";
import { BillingSuccess, BillingCancel } from "@/pages/BillingResult";
import BillingCenter from "@/pages/BillingCenter";
import MusicPage from "@/pages/MusicPage";
import SummerSlidePage from "@/pages/SummerSlidePage";
import BeatTheSummerSlidePage from "@/pages/BeatTheSummerSlidePage";
import SkillMapPage from "@/pages/SkillMapPage";
import BenchmarksPage from "@/pages/BenchmarksPage";
import MaterialsGeneratorPage from "@/pages/MaterialsGeneratorPage";
import LibraryPage from "@/pages/LibraryPage";
import ChildSetup from "@/pages/ChildSetup";
import LearningHub from "@/pages/LearningHub";
import ActivityPage from "@/pages/ActivityPage";
import AssessmentPage from "@/pages/AssessmentPage";
import BadgesPage from "@/pages/BadgesPage";
import RewardsPage from "@/pages/RewardsPage";
import SettingsPage from "@/pages/SettingsPage";
import PricingPage from "@/pages/PricingPage";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import AdminDashboard from "@/pages/AdminDashboard";
import PlatformAdminDashboard from "@/pages/PlatformAdminDashboard";
import FamilyMode from "@/pages/FamilyMode";
import OrganizationDashboard from "@/pages/OrganizationDashboard";
import OrganizationOnboarding from "@/pages/OrganizationOnboarding";
import TeacherDashboard from "@/pages/TeacherDashboard";
import HomeschoolDashboard from "@/pages/HomeschoolDashboard";
import OrgAdminDashboard from "@/pages/OrgAdminDashboard";
import OrgAdminDashboardNew from "@/pages/OrgAdminDashboardNew";
import SightWordsPage from "@/pages/SightWordsPage";
import StudyHistoryPage from "@/pages/StudyHistoryPage";
import TimedReadingPage from "@/pages/TimedReadingPage";
import TimeStats from "@/pages/TimeStats";
import WeeklyChallenges from "@/pages/WeeklyChallenges";
import LeaderboardPage from "@/pages/LeaderboardPage";
import HelpCenter from "@/pages/HelpCenter";
import StoriesPage from "@/pages/StoriesPage";
import InteractiveGames from "@/pages/InteractiveGames";
import MultiplayerGames from "@/pages/MultiplayerGames";
import CollaborativePage from "@/pages/CollaborativePage";
import BibleStoryActivity from "@/pages/BibleStoryActivity";
import FamilyStoryTime from "@/pages/FamilyStoryTime";
import BibleStoriesPage from "@/pages/BibleStoriesPage";
import BibleJourneyPage from "@/pages/BibleJourneyPage";
import BibleChapterReading from "@/pages/BibleChapterReading";
import AdaptiveLearningPage from "@/pages/AdaptiveLearningPage";
import TraceWritingPage from "@/pages/TraceWritingPage";
import AdaptiveAICurriculum from "@/pages/AdaptiveAICurriculum";
import KiddomMathPage from "@/pages/KiddomMathPage";
import AdminUserProfile from "@/pages/AdminUserProfile";
import CareersPage from "@/pages/CareersPage";
import OfferResponsePage from "@/pages/OfferResponsePage";
import SocialHubPage from "@/pages/SocialHubPage";
import HREmployeeEditPage from "@/pages/HREmployeeEditPage";
import MyHRProfilePage from "@/pages/MyHRProfilePage";
import ResourcesPage from "@/pages/ResourcesPage";
import TeamDirectoryPage from "@/pages/TeamDirectoryPage";
import ZippieAssistant from "@/components/ZippieAssistant";
import NotificationBell from "@/components/NotificationBell";
import JobDetailPage from "@/pages/JobDetailPage";
import AdminCareersDashboard from "@/pages/AdminCareersDashboard";
import InternDashboard from "@/pages/InternDashboard";
import EmployeeChat from "@/pages/EmployeeChat";
import AdminTeam from "@/pages/AdminTeam";
import AdminSupport from "@/pages/AdminSupport";
import InternPreviewCenter from "@/pages/InternPreviewCenter";
import EmployeeCenter from "@/pages/EmployeeCenter";
import DigitalSafetyLesson from "@/pages/DigitalSafetyLesson";
import DigitalSafetyGames from "@/pages/DigitalSafetyGames";
import LanguageLearning from "@/pages/LanguageLearning";
import InitialAssessment from "@/pages/InitialAssessment";
import WritingBadges from "@/pages/WritingBadges";
import DailyWritingPrompt from "@/pages/DailyWritingPrompt";
import ParentProgressReport from "@/pages/ParentProgressReport";
import GoogleClassroomIntegration from "@/pages/GoogleClassroomIntegration";
import CurriculumFlow from "@/pages/CurriculumFlow";
import OrganizationDetailView from "@/pages/OrganizationDetailView";
import MobileStudentApp from "@/pages/MobileStudentApp";
import MobileParentApp from "@/pages/MobileParentApp";
import MobileAppPage from "@/pages/MobileAppPage";
import EarlyLearning from "@/pages/EarlyLearning";
import SELCurriculumPage from "@/pages/SELCurriculumPage";
import SELLessonPage from "@/pages/SELLessonPage";
import ConceptImageSeedPage from "@/pages/ConceptImageSeedPage";
import SocialLinksAdminPage from "@/pages/SocialLinksAdminPage";
import TrophyRoomPage from "@/pages/TrophyRoomPage";
import CriticalThinkingPage from "@/pages/CriticalThinkingPage";
import PictureDictionaryPage from "@/pages/PictureDictionaryPage";
import TeamMeetingsPage from "@/pages/TeamMeetingsPage";
import MeetingRoom from "@/pages/MeetingRoom";
import ExploreLearnPage from "@/pages/ExploreLearnPage";
import ExploreLessonPage from "@/pages/ExploreLessonPage";
import LessonSearchPage from "@/pages/LessonSearchPage";
import LessonLibraryPage from "@/pages/LessonLibraryPage";
import LibraryLessonPlayer from "@/pages/LibraryLessonPlayer";
import NotificationEnrollPrompt from "@/components/NotificationEnrollPrompt";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";
import LearnPage from "@/pages/LearnPage";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";
import StaffHeartbeat from "@/components/StaffHeartbeat";
import GlobalSearch from "@/components/GlobalSearch";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import EmployeeResourcesPage from "@/pages/EmployeeResourcesPage";
import EquipmentRequestPage from "@/pages/EquipmentRequestPage";
import HRInventoryPage from "@/pages/HRInventoryPage";
import AdminChrome from "@/components/AdminChrome";
import UserProfilePage from "@/pages/UserProfilePage";
import TwoFactorSettingsPage from "@/pages/TwoFactorSettingsPage";

// Christian Education Module
import ChristianEducationLanding from "@/pages/ChristianEducationLanding";
import ChristianRegister from "@/pages/ChristianRegister";
import ChristianPictureLogin from "@/pages/ChristianPictureLogin";
import ChristianParentLogin from "@/pages/ChristianParentLogin";
import ChristianUpgrade from "@/pages/ChristianUpgrade";
import ChristianChildDashboard from "@/pages/ChristianChildDashboard";
import ChristianParentDashboard from "@/pages/ChristianParentDashboard";
import ChristianAddChild from "@/pages/ChristianAddChild";
import ChristianLesson from "@/pages/ChristianLesson";
import ChristianWorshipSongs from "@/pages/ChristianWorshipSongs";
import ChristianGallery from "@/pages/ChristianGallery";
import ReferralPage from "@/pages/ReferralPage";
import WordPuzzles from "@/pages/WordPuzzles";

// Register Service Worker for PWA/Offline support
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', registration.scope);
    } catch (error) {
      console.log('Service Worker registration failed:', error);
    }
  }
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, allowStaff = false, requireOrg = false }) => {
  const { isAuthenticated, isAdmin, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Student mode (picture-login / student-code login) is child-scoped: even if the
  // underlying parent is an employee/intern/admin, the child session must NOT be
  // able to walk into admin, employee, or team surfaces without re-authenticating.
  const isStudentSession = user?.account_type === 'student';
  const path = location.pathname || '';
  const isRestrictedPath = path.startsWith('/admin')
    || path.startsWith('/employee')
    || path.startsWith('/team')
    || path.startsWith('/social-hub')
    || path.startsWith('/organization/');
  if (isStudentSession && (requireAdmin || isRestrictedPath)) {
    return <Navigate to="/login?reauth=1" replace />;
  }

  // Admin routes can optionally allow "staff" (hired interns / employees) to enter.
  // This is needed so intern task-links into /admin?tab=... don't redirect them out.
  if (requireAdmin && !isAdmin) {
    const isStaff = !!(user?.is_staff || user?.intern_role);
    if (!(allowStaff && isStaff)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (requireOrg && !user?.organization_id) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Christian Module Protected Route - allows both parent auth and child picture login
const ChristianProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const [isChildAuth, setIsChildAuth] = useState(false);
  const [checkingChild, setCheckingChild] = useState(true);
  
  useEffect(() => {
    // Check if child is logged in via picture login
    const christianChild = localStorage.getItem('christian_child');
    const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
    
    if (christianChild && token) {
      setIsChildAuth(true);
    }
    setCheckingChild(false);
  }, []);
  
  if (loading || checkingChild) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-400 border-t-transparent"></div>
      </div>
    );
  }
  
  // Allow access if either parent is authenticated OR child is logged in
  if (!isAuthenticated && !isChildAuth) {
    return <Navigate to="/christian/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isStudent, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Redirect students to student dashboard, others to parent dashboard
    const redirectTo = isStudent ? "/student-dashboard" : "/dashboard";
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
};

function AppRoutes() {
  // Track page views for analytics
  usePageTracking();

  return (
    <>
    <StaffHeartbeat />
    <GlobalSearch />
    <ZippieAssistant />
    <NotificationBell />
    <ImpersonationBanner />
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/customers" element={<CustomersPage />} />
      <Route path="/vs/abcmouse" element={<VsABCmouse />} />
      <Route path="/vs/homer" element={<VsHomer />} />
      <Route path="/vs/khan-academy-kids" element={<VsKhanKids />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/christian/admin/login" element={<ChristianAdminLogin />} />
      <Route path="/christian/admin" element={<ChristianAdminDashboard />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      <Route path="/billing" element={<ProtectedRoute><BillingCenter /></ProtectedRoute>} />
      <Route path="/music/:childId" element={<ProtectedRoute><MusicPage /></ProtectedRoute>} />
      <Route path="/summer-slide/:childId" element={<ProtectedRoute><SummerSlidePage /></ProtectedRoute>} />
      <Route path="/beat-the-summer-slide" element={<BeatTheSummerSlidePage />} />
      <Route path="/skill-map/:childId" element={<ProtectedRoute><SkillMapPage /></ProtectedRoute>} />
      <Route path="/benchmarks/:childId" element={<ProtectedRoute><BenchmarksPage /></ProtectedRoute>} />
      <Route path="/materials" element={<ProtectedRoute><MaterialsGeneratorPage /></ProtectedRoute>} />
      <Route path="/library/:childId" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/register/organization" element={<OrganizationRegister />} />
      <Route path="/register/homeschool" element={<PublicRoute><HomeschoolRegister /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
      <Route path="/student-dashboard" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
      <Route path="/child/new" element={<ProtectedRoute><ChildSetup /></ProtectedRoute>} />
      <Route path="/child/:childId/edit" element={<ProtectedRoute><ChildSetup /></ProtectedRoute>} />
      <Route path="/learn/:childId" element={<ProtectedRoute><LearningHub /></ProtectedRoute>} />
      <Route path="/study-history/:childId" element={<ProtectedRoute><StudyHistoryPage /></ProtectedRoute>} />
      <Route path="/activity/:activityId" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
      <Route path="/assessment/:childId/:subject" element={<ProtectedRoute><AssessmentPage /></ProtectedRoute>} />
      <Route path="/badges/:childId" element={<ProtectedRoute><BadgesPage /></ProtectedRoute>} />
      <Route path="/rewards/:childId" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
      <Route path="/time/:childId" element={<ProtectedRoute><TimeStats /></ProtectedRoute>} />
      <Route path="/challenges/:childId" element={<ProtectedRoute><WeeklyChallenges /></ProtectedRoute>} />
      <Route path="/leaderboard/:childId" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/stories/:childId" element={<ProtectedRoute><StoriesPage /></ProtectedRoute>} />
      <Route path="/games/:childId" element={<ProtectedRoute><InteractiveGames /></ProtectedRoute>} />
      <Route path="/word-puzzles/:childId" element={<ProtectedRoute><WordPuzzles /></ProtectedRoute>} />
      <Route path="/multiplayer/:childId" element={<ProtectedRoute><MultiplayerGames /></ProtectedRoute>} />
      <Route path="/collaborative/:childId" element={<ProtectedRoute><CollaborativePage /></ProtectedRoute>} />
      <Route path="/bible-story/:storyId" element={<ProtectedRoute><BibleStoryActivity /></ProtectedRoute>} />
      <Route path="/bible-stories/:childId" element={<ProtectedRoute><BibleStoriesPage /></ProtectedRoute>} />
      <Route path="/bible-journey/:childId" element={<ProtectedRoute><BibleJourneyPage /></ProtectedRoute>} />
      <Route path="/bible-chapter/:chapterId" element={<ProtectedRoute><BibleChapterReading /></ProtectedRoute>} />
      <Route path="/family-story-time" element={<ProtectedRoute><FamilyStoryTime /></ProtectedRoute>} />
      <Route path="/family-story-time/:childId" element={<ProtectedRoute><FamilyStoryTime /></ProtectedRoute>} />
      <Route path="/adaptive/:childId/:subject" element={<ProtectedRoute><AdaptiveLearningPage /></ProtectedRoute>} />
      <Route path="/trace/:childId" element={<ProtectedRoute><TraceWritingPage /></ProtectedRoute>} />
      <Route path="/trace" element={<ProtectedRoute><TraceWritingPage /></ProtectedRoute>} />
      <Route path="/ai-curriculum/:childId/:subject" element={<ProtectedRoute><AdaptiveAICurriculum /></ProtectedRoute>} />
      <Route path="/kiddom-math/:childId" element={<ProtectedRoute><KiddomMathPage /></ProtectedRoute>} />
      <Route path="/admin/user/:userId" element={<ProtectedRoute><AdminUserProfile /></ProtectedRoute>} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/careers/offer/respond" element={<OfferResponsePage />} />
      <Route path="/careers/:slug" element={<JobDetailPage />} />
      <Route path="/admin/careers" element={<ProtectedRoute><AdminChrome activeSection="careers"><AdminCareersDashboard /></AdminChrome></ProtectedRoute>} />
      <Route path="/intern/dashboard" element={<ProtectedRoute><AdminChrome activeSection="intern-dashboard"><InternDashboard /></AdminChrome></ProtectedRoute>} />
      <Route path="/employee/chat" element={<ProtectedRoute><EmployeeChat /></ProtectedRoute>} />
      <Route path="/social-hub" element={<ProtectedRoute><AdminChrome activeSection="social-hub"><SocialHubPage /></AdminChrome></ProtectedRoute>} />
      <Route path="/admin/employees/:employeeId/hr" element={<ProtectedRoute><HREmployeeEditPage /></ProtectedRoute>} />
      <Route path="/employee/hr-profile" element={<ProtectedRoute><MyHRProfilePage /></ProtectedRoute>} />
      <Route path="/employee/resources" element={<ProtectedRoute><ResourcesPage /></ProtectedRoute>} />
      <Route path="/employee/team" element={<ProtectedRoute><TeamDirectoryPage /></ProtectedRoute>} />
      <Route path="/admin/team" element={<ProtectedRoute><AdminTeam /></ProtectedRoute>} />
      <Route path="/admin/support" element={<ProtectedRoute><AdminSupport /></ProtectedRoute>} />
      <Route path="/intern/preview" element={<ProtectedRoute><InternPreviewCenter /></ProtectedRoute>} />
      <Route path="/employee/center" element={<ProtectedRoute><AdminChrome activeSection="employee-center"><EmployeeCenter /></AdminChrome></ProtectedRoute>} />
      <Route path="/employee/center/:slug" element={<ProtectedRoute><AdminChrome activeSection="employee-center"><EmployeeCenter /></AdminChrome></ProtectedRoute>} />
      <Route path="/employee/resources" element={<ProtectedRoute><AdminChrome activeSection="employee-center"><EmployeeResourcesPage /></AdminChrome></ProtectedRoute>} />
      <Route path="/employee/equipment" element={<ProtectedRoute><AdminChrome activeSection="employee-center"><EquipmentRequestPage /></AdminChrome></ProtectedRoute>} />
      <Route path="/admin/hr-inventory" element={<ProtectedRoute><AdminChrome activeSection="hr-inventory"><HRInventoryPage /></AdminChrome></ProtectedRoute>} />
      <Route path="/admin/concept-images" element={<ProtectedRoute><AdminChrome activeSection="concept-images"><ConceptImageSeedPage /></AdminChrome></ProtectedRoute>} />
      <Route path="/admin/social-links" element={<ProtectedRoute><AdminChrome activeSection="social-links"><SocialLinksAdminPage /></AdminChrome></ProtectedRoute>} />
      <Route path="/trophy/:childId" element={<ProtectedRoute><TrophyRoomPage /></ProtectedRoute>} />
      <Route path="/critical-thinking/:childId" element={<ProtectedRoute><CriticalThinkingPage /></ProtectedRoute>} />
      <Route path="/team/meetings" element={<ProtectedRoute><AdminChrome activeSection="meetings"><TeamMeetingsPage /></AdminChrome></ProtectedRoute>} />
      <Route path="/meeting/:meetingId" element={<ProtectedRoute><MeetingRoom /></ProtectedRoute>} />
      <Route path="/explore/:childId" element={<ProtectedRoute><ExploreLearnPage /></ProtectedRoute>} />
      <Route path="/explore/:childId/:module/:conceptKey" element={<ProtectedRoute><ExploreLessonPage /></ProtectedRoute>} />
      <Route path="/lessons/search" element={<ProtectedRoute><LessonSearchPage /></ProtectedRoute>} />
      <Route path="/lessons/search/:childId" element={<ProtectedRoute><LessonSearchPage /></ProtectedRoute>} />
      <Route path="/lessons/library" element={<ProtectedRoute><LessonLibraryPage /></ProtectedRoute>} />
      <Route path="/lesson/preview/:lessonId" element={<ProtectedRoute><LibraryLessonPlayer /></ProtectedRoute>} />
      <Route path="/lesson/:childId/:lessonId" element={<ProtectedRoute><LibraryLessonPlayer /></ProtectedRoute>} />
      <Route path="/notifications/settings" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
      <Route path="/security/2fa" element={<ProtectedRoute><TwoFactorSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/team/:userId/hr-info" element={<ProtectedRoute><AdminChrome activeSection="team"><UserProfilePage /></AdminChrome></ProtectedRoute>} />
      <Route path="/digital-safety/:childId" element={<ProtectedRoute><DigitalSafetyLesson /></ProtectedRoute>} />
      <Route path="/digital-safety-games/:childId" element={<ProtectedRoute><DigitalSafetyGames /></ProtectedRoute>} />
      <Route path="/digital-safety-games/:childId/:gameId" element={<ProtectedRoute><DigitalSafetyGames /></ProtectedRoute>} />
      <Route path="/language/:childId/:language" element={<ProtectedRoute><LanguageLearning /></ProtectedRoute>} />
      <Route path="/assessment/:childId/:subject" element={<ProtectedRoute><InitialAssessment /></ProtectedRoute>} />
      <Route path="/writing-badges/:childId" element={<ProtectedRoute><WritingBadges /></ProtectedRoute>} />
      <Route path="/curriculum/:childId/:subject" element={<ProtectedRoute><CurriculumFlow /></ProtectedRoute>} />
      <Route path="/daily-writing/:childId" element={<ProtectedRoute><DailyWritingPrompt /></ProtectedRoute>} />
      <Route path="/progress-report/:childId" element={<ProtectedRoute><ParentProgressReport /></ProtectedRoute>} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/referrals" element={<ReferralPage />} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/family" element={<ProtectedRoute><FamilyMode /></ProtectedRoute>} />
      
      {/* Organization Routes */}
      <Route path="/organization" element={<ProtectedRoute requireOrg><OrganizationDashboard /></ProtectedRoute>} />
      <Route path="/organization/onboarding" element={<ProtectedRoute requireOrg><OrganizationOnboarding /></ProtectedRoute>} />
      <Route path="/organization/admin" element={<ProtectedRoute requireOrg><OrgAdminDashboardNew /></ProtectedRoute>} />
      <Route path="/organization/admin/legacy" element={<ProtectedRoute requireOrg><OrgAdminDashboard /></ProtectedRoute>} />
      <Route path="/teacher" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/teacher/google-classroom" element={<ProtectedRoute><GoogleClassroomIntegration /></ProtectedRoute>} />
      <Route path="/homeschool" element={<ProtectedRoute><HomeschoolDashboard /></ProtectedRoute>} />
      
      {/* Reading Assessment Routes */}
      <Route path="/sight-words/:childId" element={<ProtectedRoute><SightWordsPage /></ProtectedRoute>} />
      <Route path="/timed-reading/:childId" element={<ProtectedRoute><TimedReadingPage /></ProtectedRoute>} />
      <Route path="/early-learning/:childId" element={<ProtectedRoute><EarlyLearning /></ProtectedRoute>} />
      <Route path="/sel/:childId" element={<ProtectedRoute><SELCurriculumPage /></ProtectedRoute>} />
      <Route path="/sel/:childId/lesson/:lessonId" element={<ProtectedRoute><SELLessonPage /></ProtectedRoute>} />
      <Route path="/picture-dictionary" element={<PictureDictionaryPage />} />
      <Route path="/picture-dictionary/:childId" element={<PictureDictionaryPage />} />
      <Route path="/skill-lessons/:childId" element={<ProtectedRoute><LearnPage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute requireAdmin allowStaff><PlatformAdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/legacy" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/organization/:orgId" element={<ProtectedRoute requireAdmin><OrganizationDetailView /></ProtectedRoute>} />
      
      {/* Mobile App Routes */}
      <Route path="/mobile" element={<ProtectedRoute><MobileParentApp /></ProtectedRoute>} />
      <Route path="/mobile/student" element={<ProtectedRoute><MobileStudentApp /></ProtectedRoute>} />
      <Route path="/app" element={<MobileAppPage />} />
      <Route path="/install" element={<MobileAppPage />} />
      <Route path="/download" element={<MobileAppPage />} />
      
      {/* Christian Education Module Routes */}
      <Route path="/christian" element={<ChristianEducationLanding />} />
      <Route path="/christian/register" element={<ChristianRegister />} />
      <Route path="/christian/login" element={<ChristianParentLogin />} />
      <Route path="/christian/kid-login" element={<ChristianPictureLogin />} />
      <Route path="/christian/upgrade" element={<ChristianUpgrade />} />
      <Route path="/christian/parent" element={<ChristianProtectedRoute><ChristianParentDashboard /></ChristianProtectedRoute>} />
      <Route path="/christian/add-child" element={<ChristianProtectedRoute><ChristianAddChild /></ChristianProtectedRoute>} />
      <Route path="/christian/child/:childId" element={<ChristianProtectedRoute><ChristianChildDashboard /></ChristianProtectedRoute>} />
      <Route path="/christian/lesson/:childId/:weekNumber" element={<ChristianProtectedRoute><ChristianLesson /></ChristianProtectedRoute>} />
      <Route path="/christian/songs/:childId" element={<ChristianProtectedRoute><ChristianWorshipSongs /></ChristianProtectedRoute>} />
      <Route path="/christian/songs" element={<ChristianWorshipSongs />} />
      <Route path="/christian/gallery/:childId" element={<ChristianProtectedRoute><ChristianGallery /></ChristianProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <ThemeProvider>
      <BrandThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <PermissionsProvider>
              <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-center" richColors />
                <InstallPWAPrompt />
                <NotificationEnrollPrompt />
              </BrowserRouter>
            </PermissionsProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrandThemeProvider>
    </ThemeProvider>
  );
}

export default App;
